import chatIcon from '@/assets/icons/chat-icon.svg';
import { SvgIcon } from '@/components/UI/Common/SvgIcon';
import {
  getPlainTextFromHtml,
  highlightPlainText,
  highlightSearchInHtml,
  looksLikeHtmlMessage,
  replaceLinksInText,
  sanitizeMessageHtml,
} from '@/utils/messageContent';
import { getBackUrl, resolvePortalUrl } from '@/utils/helpers';
import { ReactionType, type Message } from '@/types/chats';
import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useToggleReaction } from '@/queries/chats';
import { ChatsImageViewer } from './ChatsImageViewer';
import { useBreakpoints } from '@/hooks/useBreakpoints';

const PREVIEW_LIMIT = 220;

/** Temporary: hide reactions in UI without removing the feature. */
const SHOW_REACTIONS = false;

type ChatsMessageMode = 'preview' | 'full';

type ChatsMessageContextValue = Message & {
  chatTitle: string | undefined;
  searchQuery?: string;
  mode: ChatsMessageMode;
  setMode: (mode: ChatsMessageMode) => void;
  rawText: string;
  parsedText: ReactNode;
  isHtmlContent: boolean;
  sanitizedHtml: string;
  truncatedHtmlPreview: ReactNode;
  truncatedPlainPreview: ReactNode;
  isTruncated: boolean;
  handleReactions: VoidFunction;
  isLiked: boolean;
  reactionsCount: number;
  isMobile: boolean;
};

const ChatsMessageContext = createContext<ChatsMessageContextValue | null>(null);

const useChatsMessageContext = () => {
  const context = useContext(ChatsMessageContext);

  if (!context) {
    throw new Error('ChatsMessage compound components should be used inside ChatsMessage');
  }

  return context;
};

const ChatsMessage = ({
  message,
  chatTitle,
  searchQuery,
  children,
  previewLimit = PREVIEW_LIMIT,
}: {
  message?: Message;
  chatTitle?: string;
  searchQuery?: string;
  children: React.ReactNode;
  previewLimit?: number;
  isLiked?: boolean;
}) => {
  const [mode, setMode] = useState<ChatsMessageMode>('preview');
  const rawText = message?.text ?? '';
  const isHtmlContent = looksLikeHtmlMessage(rawText);
  const hasSearchQuery = Boolean(searchQuery?.trim());
  const sanitizedHtml = useMemo(() => {
    if (!isHtmlContent) return '';
    const sanitized = sanitizeMessageHtml(rawText);
    return highlightSearchInHtml(sanitized, searchQuery);
  }, [isHtmlContent, rawText, searchQuery]);
  const { isMobile } = useBreakpoints();
  const plainTextFromHtml = useMemo(
    () => (isHtmlContent ? getPlainTextFromHtml(rawText) : ''),
    [isHtmlContent, rawText],
  );
  const plainLen = isHtmlContent ? plainTextFromHtml.length : rawText.length;
  const isTruncated = !hasSearchQuery && plainLen > previewLimit;
  const truncatedHtmlPreview = isTruncated
    ? highlightPlainText(`${plainTextFromHtml.slice(0, previewLimit).trimEnd()}...`, searchQuery)
    : '';
  const truncatedPlainPreview = isTruncated
    ? highlightPlainText(`${rawText.slice(0, previewLimit).trimEnd()}...`, searchQuery)
    : '';

  const parsedText = useMemo(() => {
    if (isHtmlContent) {
      return (
        <div className={messageBodyClassName} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
      );
    }

    return replaceLinksInText(rawText, searchQuery);
  }, [isHtmlContent, sanitizedHtml, rawText, searchQuery]);

  const { mutate: toggleReaction } = useToggleReaction(message?.chatId ?? '');

  if (!message) return null;

  const reactionsCount = message.reactions.filter(
    (reaction) => reaction.type === ReactionType.Heart,
  ).length;

  const handleReactions = () => {
    toggleReaction({ messageId: message.id, type: ReactionType.Heart });
  };
  const { isLiked } = message;

  const contextValue: ChatsMessageContextValue = {
    ...message,
    chatTitle,
    searchQuery,
    mode: hasSearchQuery ? 'full' : mode,
    setMode,
    rawText,
    parsedText,
    isHtmlContent,
    sanitizedHtml,
    truncatedHtmlPreview,
    truncatedPlainPreview,
    isTruncated,
    handleReactions,
    isLiked,
    reactionsCount,
    isMobile,
  };

  return (
    <ChatsMessageContext.Provider value={contextValue}>
      <div
        id="chats-message"
        className="relative grid min-w-0 gap-4 rounded-md bg-white px-4 py-3 md:gap-2.5 md:border md:border-[#ebebef] md:shadow-[0_1px_2px_rgba(15,15,20,0.04)] md:px-4 md:py-3"
      >
        {children}
      </div>
    </ChatsMessageContext.Provider>
  );
};

const Reactions = () => {
  const { isLiked, reactionsCount, handleReactions, isMobile } = useChatsMessageContext();

  if (!SHOW_REACTIONS) return null;

  const iconColor = isLiked ? '#F44235' : reactionsCount > 0 ? '#F44235' : '#71717A';
  const containerStyle = isLiked
    ? 'bg-lightRed border-lightRed hover:brightness-95'
    : 'bg-transparent border-[#e4e4e7] hover:bg-lightRed hover:border-lightRed';

  return (
    <div
      onClick={handleReactions}
      className={`group flex shrink-0 cursor-pointer items-center gap-2 rounded-md border p-1 transition-all duration-200 ease-out md:gap-1.5 md:px-1.5 md:py-0.5 ${containerStyle}`}
    >
      <span className="inline-flex transition-transform duration-200 ease-out group-hover:scale-110">
        <SvgIcon size={isMobile ? 15 : 16} name="heart" color={iconColor} />
      </span>
      <span className={`${isLiked ? 'text-danger' : 'text-darkGray'} max-md:text-xs md:text-xs`}>
        {reactionsCount}
      </span>
    </div>
  );
};

const Title = () => {
  const { chatTitle } = useChatsMessageContext();
  return <h3 className="text-xl font-semibold">{chatTitle}</h3>;
};

const Info = () => {
  const { author, communityName, authorAvatar } = useChatsMessageContext();
  return (
    <div className="flex min-w-0 items-center justify-between gap-2 md:gap-3">
      <div className="flex min-w-0 items-center gap-2.5 md:gap-2">
        <img
          className="h-[30px] w-[30px] shrink-0 rounded-full object-cover md:h-8 md:w-8"
          src={resolvePortalUrl(authorAvatar) || chatIcon}
          alt=""
        />
        <div className="min-w-0">
          <h3 className="truncate text-sm font-medium md:text-sm">{author}</h3>
        </div>
      </div>
      {communityName ? (
        <div className="max-w-[40%] shrink truncate text-right text-sm text-lightGray md:text-xs">
          {communityName}
        </div>
      ) : null}
    </div>
  );
};

const MessageDate = () => {
  const { createdAt } = useChatsMessageContext();
  const formatted = new Date(createdAt).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <div className="min-w-0 py-2 text-right text-lightGray max-md:text-xs md:py-0 md:text-left md:text-xs md:tabular-nums">
      {formatted}
    </div>
  );
};

const messageBodyClassName =
  'text-md md:text-[15px] md:leading-relaxed md:text-[#3f3f46] flex min-w-0 flex-col gap-3 break-words md:gap-2 [&_a]:break-all [&_a]:text-black [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-gray-200 [&_blockquote]:pl-3 [&_blockquote]:text-gray-700 [&_blockquote>p]:mb-2 [&_blockquote>p]:last:mb-0 [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_li]:my-0.5 [&_li>p]:min-h-0 [&_mark]:rounded-sm [&_mark]:bg-amber-200/90 [&_mark]:px-0.5 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:min-h-[1em] [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-gray-100 [&_pre]:p-3 [&_ul]:list-disc [&_ul]:pl-6';

const Message = () => {
  const {
    mode,
    setMode,
    parsedText,
    isHtmlContent,
    sanitizedHtml,
    truncatedHtmlPreview,
    truncatedPlainPreview,
    isTruncated,
  } = useChatsMessageContext();

  const handleReadMore = () => {
    setMode('full');
  };

  if (isHtmlContent) {
    if (!isTruncated || mode === 'full') {
      return (
        <div className={messageBodyClassName} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
      );
    }
    return (
      <div className="min-w-0">
        <p className="whitespace-pre-wrap break-words text-md md:text-[15px] md:leading-relaxed md:text-[#3f3f46]">
          {truncatedHtmlPreview}
        </p>
        <ChatsMessage.More onReadMore={handleReadMore} />
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {isTruncated && mode === 'preview' ? (
        <p className="whitespace-pre-wrap break-words text-md md:text-[15px] md:leading-relaxed md:text-[#3f3f46]">
          {truncatedPlainPreview}
        </p>
      ) : (
        <div className="whitespace-pre-wrap break-words text-md md:text-[15px] md:leading-relaxed md:text-[#3f3f46]">
          {parsedText}
        </div>
      )}
      {mode === 'preview' && <ChatsMessage.More onReadMore={handleReadMore} />}
    </div>
  );
};

const More = ({ onReadMore }: { onReadMore: () => void }) => {
  const { isTruncated } = useChatsMessageContext();

  if (!isTruncated) return null;

  return (
    <button
      type="button"
      onClick={onReadMore}
      className="mt-1 block cursor-pointer text-black underline"
    >
      Подробнее
    </button>
  );
};

const Images = () => {
  const { images } = useChatsMessageContext();
  const backUrl = getBackUrl();
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {images.map((image, index) => (
          <img
            key={image.id}
            src={`${backUrl}/uploads/${image.upload.path}`}
            className="max-w-full max-h-80 object-contain rounded-md cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => setViewerIndex(index)}
          />
        ))}
      </div>
      <ChatsImageViewer
        images={images}
        initialIndex={viewerIndex ?? 0}
        isOpen={viewerIndex !== null}
        onClose={() => setViewerIndex(null)}
      />
    </>
  );
};

ChatsMessage.Info = Info;
ChatsMessage.Date = MessageDate;
ChatsMessage.Message = Message;
ChatsMessage.More = More;
ChatsMessage.Title = Title;
ChatsMessage.Images = Images;
ChatsMessage.Reactions = Reactions;

export default ChatsMessage;
